import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Fab,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../../assests/logo.png";
import { getCurrentBranchId, getCurrentUser } from "../../../utils/auth";
import { isFixedQr } from "../../../utils/plan";
import { generateQrDataUrl, resolveQrDataUrl } from "../../../utils/qr";
import { getQRCodes, createQRCode, getTutorialProgress, saveTutorialProgress, getServiceSettings, validatePhoneNumber } from "../../../api/api";
import { normalizePhone } from "../../../utils/phone";
import OnboardingTutorialOverlay from "../../../components/OnboardingTutorialOverlay";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import { getStorageUrl } from "../../../config/config";
const QRPage = () => {
    const { pathname, state: navState } = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [qrCode, setQrCode] = useState("");
    const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [currentQRCode, setCurrentQRCode] = useState("");

    const branchId = getCurrentBranchId();
    const [currentUserState, setCurrentUserState] = useState(getCurrentUser());
    const [qrPageTitle, setQrPageTitle] = useState("");

    // SMS queue registration - opt-in per branch, so the phone field only
    // appears where the client switched it on (each one sends a real text).
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneLoading, setPhoneLoading] = useState(false);
    const [phoneError, setPhoneError] = useState("");

    // Fixed-QR plans (e.g. Free) cannot change/regenerate their code.
    const qrLocked = isFixedQr(currentUserState);

    // Check if user has visited live queue page before
    const [hasVisitedLiveQueue, setHasVisitedLiveQueue] = useState(() => {
        return localStorage.getItem('live_queue_visited') === 'true';
    });

    // Check if onboarding is completed
    const isOnboardingCompleted = currentUserState?.onboarding_completed === 1;
    
    // Show instructions only if onboarding is not completed and user hasn't visited live queue
    const shouldShowInstructions = !isOnboardingCompleted && !hasVisitedLiveQueue;

    const [runOnboardingTutorial, setRunOnboardingTutorial] = useState(false);
    const [tutorialProgress, setTutorialProgress] = useState(null);

    // Initialize logo URL correctly from the start to prevent flash
    const [logoUrl, setLogoUrl] = useState(() => {
        if (currentUserState && currentUserState.logo) {
            const logoFilename = currentUserState.logo;
            return getStorageUrl(`/storage/logos/${logoFilename}`);
        }
        return Logo;
    });

    // Update logo URL if currentUserState changes
    useEffect(() => {
        if (currentUserState && currentUserState.logo) {
            // Construct logo URL from storage
            const logoFilename = currentUserState.logo;
            const dynamicLogoUrl = getStorageUrl(`/storage/logos/${logoFilename}`);
            setLogoUrl(dynamicLogoUrl);
        } else {
            // Fallback to default logo if no user logo is available
            setLogoUrl(Logo);
        }
    }, [currentUserState]);

    // Check for live queue visit status and onboarding completion when component mounts or location changes
    useEffect(() => {
        setHasVisitedLiveQueue(localStorage.getItem('live_queue_visited') === 'true');
        // Update currentUserState from localStorage to get latest onboarding_completed
        const storedUser = getCurrentUser();
        if (storedUser) {
            setCurrentUserState(storedUser);
        }
    }, [pathname]);

    useEffect(() => {
        getServiceSettings(branchId)
            .then((res) => {
                if (res?.success) {
                    setQrPageTitle(res.data?.qr_page_title || "");
                    setSmsEnabled(res.data?.sms_enabled === true);
                }
            })
            .catch(() => {});
    }, [branchId]);

    useEffect(() => {
        if (!currentUserState?.is_free_trial) return;
        getTutorialProgress(branchId)
            .then((res) => {
                if (res?.success && res?.progress) setTutorialProgress(res.progress);
            })
            .catch(() => {});
    }, [branchId, currentUserState?.is_free_trial]);

    useEffect(() => {
        // Wait until the QR image has loaded before starting the tour
        if (!qrCodeDataUrl) return;
        const fromReplay = navState?.startOnboardingTour === true;
        if (fromReplay) {
            setRunOnboardingTutorial(true);
            return;
        }
        if (!tutorialProgress || !currentUserState?.is_free_trial) return;
        if (tutorialProgress?.onboardingPhases?.dashboard !== true) return;
        if (tutorialProgress?.onboardingPhases?.qr === true) return;
        setRunOnboardingTutorial(true);
    }, [tutorialProgress, currentUserState?.is_free_trial, navState?.startOnboardingTour, qrCodeDataUrl]);

    const handleOnboardingPhaseComplete = async (page) => {
        // Only navigate to next page during initial onboarding (qr phase not yet done)
        const isInitialOnboarding = tutorialProgress?.onboardingPhases?.qr !== true;
        setRunOnboardingTutorial(false);
        try {
            const res = await getTutorialProgress(branchId);
            const progress = res?.success && res?.progress ? { ...res.progress } : {};
            progress.onboardingPhases = progress.onboardingPhases || {};
            progress.onboardingPhases[page] = true;
            await saveTutorialProgress(branchId, progress);
            setTutorialProgress(progress);
            if (isInitialOnboarding && page === "qr") navigate("/home/staff", { replace: true, state: { startOnboardingTour: true } });
        } catch (e) {
            console.error("Failed to save onboarding phase:", e);
            if (isInitialOnboarding && page === "qr") navigate("/home/staff", { replace: true, state: { startOnboardingTour: true } });
        }
    };

    // Hand the customer over to the SMS flow with the phone number already
    // validated, so they land straight on service selection.
    const handlePhoneSubmit = async () => {
        const msisdn = normalizePhone(phoneNumber);
        if (!msisdn) {
            setPhoneError("Ugyldig telefonnummer. Skriv f.eks. +47 123 45 678.");
            return;
        }

        setPhoneLoading(true);
        setPhoneError("");

        try {
            const response = await validatePhoneNumber(msisdn, branchId);
            // Carry the branch code so the SMS pages resolve the right branch
            // even on a device that was never logged in.
            const target = currentQRCode
                ? `/home/sms-customer-view?branch=${encodeURIComponent(currentQRCode)}`
                : "/home/sms-customer-view";
            navigate(target, {
                state: {
                    phoneNumber: msisdn,
                    customer: response.customer,
                    returnTo: pathname,
                },
            });
        } catch (err) {
            console.error("Phone validation failed", err);
            setPhoneError(
                err.response?.status === 403
                    ? "SMS-registrering er ikke aktivert for denne avdelingen."
                    : "Ugyldig telefonnummer. Prøv igjen."
            );
            setPhoneLoading(false);
        }
    };

    // Generate QR Code
    const generateQRCode = async () => {
        if (!qrCode.trim()) {
            setError("Please enter a code");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const dataUrl = await generateQrDataUrl(qrCode);

            setQrCodeDataUrl(dataUrl);
            setCurrentQRCode(qrCode);

            // Save to database
            await createQRCode({
                code: qrCode,
                branch_id: branchId,
                qr_data_url: dataUrl,
            });

            setSuccess(`QR Code generated and saved successfully!`);
            setModalOpen(false); // Close modal after success
        } catch (err) {
            console.error("Error generating QR code:", err);
            
            // Extract specific error message from backend response
            let errorMessage = "Failed to generate QR code";
            
            if (err.response?.data?.errors) {
                // Handle validation errors (422)
                const errors = err.response.data.errors;
                if (errors.code) {
                    errorMessage = errors.code[0]; // Get first error message for code field
                } else if (errors.branch_id) {
                    errorMessage = errors.branch_id[0];
                } else {
                    // Get first error from any field
                    const firstErrorKey = Object.keys(errors)[0];
                    errorMessage = errors[firstErrorKey][0];
                }
            } else if (err.response?.data?.message) {
                // Handle general error messages
                errorMessage = err.response.data.message;
            } else if (err.message) {
                // Handle network or other errors
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Load current QR code from database
    const loadCurrentQRCode = async () => {
        try {
            const data = await getQRCodes(branchId);
            if (data && data.length > 0) {
                const currentCode =
                    data.find((code) => code.is_active) || data[0];
                const dataUrl = await resolveQrDataUrl(
                    currentCode.qr_data_url,
                    currentCode.code
                );
                if (dataUrl) {
                    setQrCodeDataUrl(dataUrl);
                    setCurrentQRCode(currentCode.code);
                }
                // Backfill branches that have a code but no stored image, so
                // the customer-facing screens get it as well.
                if (dataUrl && !currentCode.qr_data_url && currentCode.code) {
                    createQRCode({
                        code: currentCode.code,
                        branch_id: branchId,
                        qr_data_url: dataUrl,
                    }).catch((err) =>
                        console.error("Error backfilling QR code:", err)
                    );
                }
            }
        } catch (err) {
            console.error("Error loading current QR code:", err);
        }
    };

    // Load existing codes on component mount
    useEffect(() => {
        loadCurrentQRCode();
    }, []);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess("");
                setError("");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            bgcolor="#fff"
            px={2}
            py={4}
            minHeight="100%"
            width="100%"
            overflow="auto"
            sx={{
                height: "100%",
                maxHeight: "none",
            }}
        >
            {/* Header */}
            <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: "2rem", sm: "3rem", md: "4rem" } }}
            >
                {qrPageTitle || "Queue Ticket"}
            </Typography>

            {/* Logo - Dynamic based on logged-in user */}
            <Box 
                mb={3} 
                display={isMobile ? "flex" : "grid"} 
                flexDirection={isMobile ? "column" : undefined}
                gridTemplateColumns={isMobile ? undefined : "1fr auto 1fr"}
                alignItems="center" 
                justifyItems="center"
                width="100%"
                gap={isMobile ? 2 : 0}
            >
                {/* Left Spacer for Grid */}
                {!isMobile && <Box />}

                <img
                    src={logoUrl}
                    alt="Branch Logo"
                    style={{ width: isMobile ? 180 : 300, maxWidth: "90%" }}
                    onError={(e) => {
                        // Fallback to default logo if dynamic logo fails to load
                        e.target.src = Logo;
                    }}
                />
                
                {/* <Typography 
                    variant="body1" 
                    color="text.primary" 
                    sx={{ 
                        maxWidth: 250,
                        textAlign: isMobile ? "center" : "left",
                        fontWeight: 500,
                        justifySelf: isMobile ? "auto" : "center", // Center in the right column
                    }}
                >
                    This is the QR code your customers will see on the screen. Use your phone to scan it and join the queue
                </Typography> */}
            </Box>

            {/* Instructions Text - Only show if onboarding not completed and user hasn't visited live queue page */}
            {shouldShowInstructions && (
                <Box textAlign="center" mt={2} mb={3}>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        color="primary"
                        gutterBottom
                        sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
                    >
                        Scan the QR Code
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            maxWidth: 600,
                            mx: "auto",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            lineHeight: 1.6,
                        }}
                    >
                        Use your mobile device's camera to scan this QR code. 
                        Customers can scan it to join your queue.
                    </Typography>
                </Box>
            )}

            {/* QR Code Display */}
            {qrCodeDataUrl ? (
                <Box textAlign="center" mt={2} data-onboarding-id="onboarding-qr-area">
                    <img
                        src={qrCodeDataUrl}
                        alt="QR Code"
                        style={{
                            width: isMobile ? 350 : 600,
                            maxWidth: "100%",
                            border: "2px solid #e0e0e0",
                            borderRadius: 8,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        }}
                    />
                    {/* Code label hidden */}
                    {shouldShowInstructions && (
                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            mt={2}
                            sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                        >
                            📱 Open your phone's camera app and point it at this QR code
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box textAlign="center" mt={4} data-onboarding-id="onboarding-qr-area">
                    <Typography variant="h6" color="text.secondary" mb={2}>
                        No QR Code Generated
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click the settings button to generate a QR code
                    </Typography>
                </Box>
            )}

            {/* SMS alternative - only for branches that opted in */}
            {smsEnabled && (
                <Box
                    sx={{
                        mt: 4,
                        width: "100%",
                        maxWidth: 400,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        — eller få kønummer på SMS —
                    </Typography>
                    <TextField
                        fullWidth
                        label="Telefonnummer"
                        value={phoneNumber}
                        onChange={(e) => {
                            setPhoneNumber(e.target.value);
                            if (phoneError) setPhoneError("");
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !phoneLoading) {
                                handlePhoneSubmit();
                            }
                        }}
                        placeholder="+47 123 45 678"
                        type="tel"
                        inputMode="tel"
                        variant="outlined"
                        error={!!phoneError}
                        helperText={phoneError}
                        sx={{ mb: 2 }}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handlePhoneSubmit}
                        fullWidth
                        disabled={phoneLoading || !phoneNumber.trim()}
                    >
                        {phoneLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            "Fortsett"
                        )}
                    </Button>
                </Box>
            )}

            {/* Additional Instructions - Only show if onboarding not completed and user hasn't visited live queue page */}
            {qrCodeDataUrl && shouldShowInstructions && (
                <Box textAlign="center" mt={4} mb={2}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            maxWidth: 600,
                            mx: "auto",
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                            fontStyle: "italic",
                        }}
                    >
                        After customers scan the QR code, you can check the live queue 
                        by clicking the "LIVE QUEUE" button in the navigation bar above.
                    </Typography>
                </Box>
            )}

            {/* Settings FAB — hidden on fixed-QR plans (code cannot be changed) */}
            {!qrLocked && (
                <Fab
                    color="primary"
                    aria-label="settings"
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        right: 16,
                    }}
                    onClick={() => setModalOpen(true)}
                >
                    <SettingsIcon />
                </Fab>
            )}

            {/* Settings Modal */}
            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography variant="h6">Generate QR Code</Typography>
                        <IconButton onClick={() => setModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Enter QR Code"
                        value={qrCode}
                        onChange={(e) => setQrCode(e.target.value)}
                        placeholder="e.g., MAIN001, DOWN001"
                        sx={{ mb: 3, mt: 2 }}
                        helperText="Enter a unique code for this QR code"
                    />

                    {/* Messages */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={generateQRCode}
                        disabled={loading || !qrCode.trim()}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Generating...
                            </>
                        ) : (
                            "Generate QR Code"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <OnboardingTutorialOverlay
                page="qr"
                run={runOnboardingTutorial}
                onPhaseComplete={handleOnboardingPhaseComplete}
                onClose={() => setRunOnboardingTutorial(false)}
            />
        </Box>
    );
};

export default QRPage;
